import { createTheme } from '@mui/material/styles';

// Chemelex brand tokens (from the UI reference dashboard).
export const brandColors = {
  // The action colour: buttons, focus rings, selected states — everything the
  // user can press. Set as MUI's `palette.primary`, so `variant="contained"`
  // and `color="primary"` pick it up without a per-component override, and it
  // is what FilledButton defaults to.
  primary: '#0077b6',
  navy: '#1F3864', // brand chrome — headers, KPI accents
  orange: '#E8602C', // secondary — CTAs
  blue: '#1B75BC', // informational accents
  green: '#2E7D55', // positive deltas
  sky: '#0277BD', // secondary informational accent
  amber: '#F59E0B', // warning flags
  red: '#C00000', // error/alert accents
  // Negative gap-to-AOP reads in blue, not red: a shortfall against plan is a
  // figure to act on, not an error state, and the app already spends red on
  // genuine alerts. `gapFill` is the deeper tone used when the colour fills a
  // whole cell behind white text (contrast), `gapNegative` when it tints text.
  gapNegative: '#1B75BC',
  gapFill: '#1F3864',
  border: '#DEE2E6',
  grey: '#6C757D',
  bg: '#F5F6FA',
};

export const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: brandColors.primary,
      // Steps of the same hue rather than arbitrary tints, so hover/active
      // states stay in the family.
      light: '#3392c7',
      dark: '#005a8a',
      contrastText: '#ffffff',
    },
    secondary: {
      main: brandColors.orange,
    },
    background: {
      default: brandColors.bg,
      paper: '#ffffff',
    },
    error: {
      main: brandColors.red,
    },
    warning: {
      main: brandColors.amber,
    },
    success: {
      main: brandColors.green,
    },
    info: {
      main: brandColors.blue,
    },
  },
  typography: {
    // Montserrat, then the platform sans — no Inter/Segoe in between, so a
    // machine without the webfont falls straight to its own UI sans rather than
    // to whichever of those two happens to be installed.
    fontFamily: '"Montserrat", sans-serif',
    // MUI's base is 14. The app reads at arm's length on a wall display as
    // often as on a laptop, and 14 was leaving the dense tables genuinely hard
    // to scan. Every rem-based variant scales from this one value.
    fontSize: 15,
    h4: { fontWeight: 700 },
    h5: { fontWeight: 700 },
    h6: { fontWeight: 600 },
  },
  // App-wide chrome. These are the defaults every surface inherits, so a value
  // set here is the cheapest way to lift the whole app at once — the
  // alternative is the same override repeated per component, which is how the
  // UI drifted into looking hand-assembled in the first place.
  shape: { borderRadius: 10 },
  components: {
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        // Pill buttons with real padding. MUI's default is a squarish 4px box
        // with a drop shadow on `contained` — the single most dated element on
        // any un-themed MUI page.
        root: {
          textTransform: 'none',
          fontWeight: 600,
          borderRadius: 999,
          paddingInline: 18,
        },
      },
    },
    MuiTextField: {
      defaultProps: { size: 'small' },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          backgroundColor: '#fff',
          '& .MuiOutlinedInput-notchedOutline': { borderColor: '#DFE3EA' },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#C3CAD6' },
        },
        notchedOutline: { borderRadius: 10 },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: { borderRadius: 14, boxShadow: '0 1px 2px rgba(16, 24, 40, 0.04)' },
      },
    },
    MuiPaper: {
      // The default `elevation` ramp is a stack of 1970s-blur drop shadows.
      // One hairline shadow, applied at every level, matches SURFACE_SHADOW in
      // tableChrome so a Paper and a themed panel sit at the same depth.
      styleOverrides: {
        elevation1: { boxShadow: '0 1px 2px rgba(16, 24, 40, 0.04)' },
        elevation2: { boxShadow: '0 2px 8px rgba(16, 24, 40, 0.06)' },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: { textTransform: 'none', fontWeight: 600 },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { fontWeight: 600, borderRadius: 999 },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        // Tighter than MUI's 16px default, which turned a 12-column pivot into
        // a page-and-a-half of whitespace.
        root: { paddingInline: 12 },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: { fontSize: 12, borderRadius: 8, backgroundColor: '#1F2937', padding: '6px 10px' },
      },
    },

    // ── No hard corners ───────────────────────────────────────────────────
    // `shape.borderRadius` above only reaches components that read it. A
    // handful of MUI components hard-code a square or a 4px corner of their
    // own, and those were the ones left looking cut off next to everything
    // else: the month/period field, the Month·Quarter and FY toggle groups,
    // the account popover, alerts, menus, and the outer edge of any table that
    // sits directly in a Paper. Each is rounded here, once, rather than at
    // whichever call site happened to notice.
    MuiToggleButtonGroup: {
      styleOverrides: {
        root: { borderRadius: 999, overflow: 'hidden' },
        // The group rounds its ends; MUI squares off the inner children to
        // butt them together, and grouped/first/last each need saying.
        grouped: {
          borderRadius: '999px !important',
          '&:not(:first-of-type)': { marginLeft: 0 },
        },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: { textTransform: 'none', fontWeight: 600, borderRadius: 999, paddingInline: 14 },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        // Covers the native date/month input and anything else that renders an
        // InputBase without the outlined shell.
        root: { borderRadius: 10 },
      },
    },
    MuiSelect: {
      styleOverrides: {
        outlined: { borderRadius: 10 },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: { borderRadius: 12, boxShadow: '0 12px 28px -8px rgba(16,24,40,0.16)' },
      },
    },
    MuiPopover: {
      styleOverrides: {
        paper: { borderRadius: 12, boxShadow: '0 12px 28px -8px rgba(16,24,40,0.16)' },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: { borderRadius: 14 },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: { borderRadius: 12 },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          overflow: 'hidden',
          '&:before': { display: 'none' },
          '&:first-of-type, &:last-of-type': { borderRadius: 12 },
        },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: { borderRadius: 14 },
      },
    },
  },
});

// Helper: colour a gap value (positive = green, negative = red).
export const gapColor = (value) =>
  value > 0 ? brandColors.green : value < 0 ? brandColors.gapNegative : brandColors.grey;
